import { Dispatch, SetStateAction, useCallback } from "react";

import { collapseToSingle, linkedInItemKey, SelectedLinkedInItem } from "./singleSelect";

interface UseSingleSelectWrappersArgs {
	selectedContactIds: number[];
	setSelectedContactIds: Dispatch<SetStateAction<number[]>>;
	selectedLinkedInItems: SelectedLinkedInItem[];
	setSelectedLinkedInItems: Dispatch<SetStateAction<SelectedLinkedInItem[]>>;
}

interface UseSingleSelectWrappersResult {
	selectContactSingle: Dispatch<SetStateAction<number[]>>;
	selectLinkedInSingle: Dispatch<SetStateAction<SelectedLinkedInItem[]>>;
}

/**
 * Single-select ("compose") wrappers for GlobalSearchModal: collapse every write to exactly one
 * selection and clear the opposite bucket (contacts vs LinkedIn are mutually exclusive). Passing
 * these in place of the raw setters lets each tab keep its existing per-row / select-all handlers
 * untouched. Extracted from GlobalSearchModal to keep that component under the file-size limit.
 */
export function useSingleSelectWrappers({
	selectedContactIds,
	setSelectedContactIds,
	selectedLinkedInItems,
	setSelectedLinkedInItems,
}: UseSingleSelectWrappersArgs): UseSingleSelectWrappersResult {
	const selectContactSingle = useCallback<Dispatch<SetStateAction<number[]>>>(
		(action) => {
			const next = typeof action === "function" ? action(selectedContactIds) : action;
			const chosen = collapseToSingle(selectedContactIds, next, (id) => id);

			setSelectedLinkedInItems([]);
			setSelectedContactIds(chosen === undefined ? [] : [chosen]);
		},
		[selectedContactIds, setSelectedContactIds, setSelectedLinkedInItems]
	);

	const selectLinkedInSingle = useCallback<Dispatch<SetStateAction<SelectedLinkedInItem[]>>>(
		(action) => {
			const next = typeof action === "function" ? action(selectedLinkedInItems) : action;
			const chosen = collapseToSingle(selectedLinkedInItems, next, linkedInItemKey);

			setSelectedContactIds([]);
			setSelectedLinkedInItems(chosen === undefined ? [] : [chosen]);
		},
		[selectedLinkedInItems, setSelectedContactIds, setSelectedLinkedInItems]
	);

	return { selectContactSingle, selectLinkedInSingle };
}
